<template>
  <div class="personalTimeTracking">
    <div class="grid">
      <Card>
        <template #title>
          {{ $t('time_tracking') }}
          <div class="personalTimeTracking" v-if="appointment && appointment.project">
            {{ $t('time_tracking') }} bei {{ appointment.project.customer.firstname }}
            {{ appointment.project.customer.lastname }}
          </div>
        </template>
        <template #content>
          <div class="time-control-container">
            <div class="time-display" v-if="duration">
              {{ duration }}
            </div>
            <div class="time-display" v-else-if="startTime && timeTrackingState !== 'STOPPED'">
              <Spinner />
            </div>
            <div class="button-container">
              <Button
                :class="[
                  'circular-button',
                  timeTrackingState === 'STARTED' ? 'stop-button' : 'play-button',
                  timeTrackingState === 'STARTED' ? 'btn-danger' : 'btn-primary',
                ]"
                @click="toggleTimeTracking"
                :icon="timeTrackingState === 'STARTED' ? 'fa fa-stop' : 'fa fa-play'"
              >
              </Button>
            </div>
          </div>

          <Accordion class="mt-3">
            <AccordionTab header="Erfasste Zeiten">
              <MyTimeTrackingTable />
            </AccordionTab>
          </Accordion>

          <Accordion class="mt-3">
            <AccordionTab header="Krankmeldung, SOKA und Urlaubsanträge">
              <MyEmployeeRequestTable />
            </AccordionTab>
          </Accordion>
        </template>
      </Card>
    </div>
    <Toast />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import MyTimeTrackingTable from '@/components/MyTimeTrackingTable';
import MyEmployeeRequestTable from '@/components/MyEmployeeRequestTable';
import Spinner from '@/components/Spinner.vue';
import Toast from 'primevue/toast';

export default {
  name: 'TimeTrackingCard',
  components: {
    Card,
    Button,
    Accordion,
    AccordionTab,
    MyTimeTrackingTable,
    MyEmployeeRequestTable,
    Spinner,
    Toast,
  },
  props: {
    project: { type: Object },
    appointment: { type: Object },
  },
  data() {
    return {
      startTime: null,
      stopTime: null,
      currentTime: null,
      timer: null,
      timeTrackingNumber: null,
      timeTrackingState: null,
    };
  },

  computed: {
    ...mapGetters(['getTimeTrackings', 'getTimeTrackingByNumber']),
    ...mapGetters('auth', ['getCurrentUser']),

    duration() {
      if (
        !this.startTime ||
        !this.currentTime ||
        !this.timeTrackingState ||
        this.timeTrackingState !== 'STARTED'
      )
        return null;

      const diff = this.$dayjs(this.currentTime).diff(this.$dayjs(this.startTime), 'second');
      const hours = String(Math.floor(diff / 3600)).padStart(2, '0');
      const minutes = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
      const seconds = String(diff % 60).padStart(2, '0');
      // console.log('currentTime',this.startTime, this.currentTime);
      return `${hours}:${minutes}:${seconds}`;
    },

    latestTimeTracking() {
      return this.getTimeTrackings.find((tt) => tt.timeTrackingState === 'STARTED');
    },
  },

  methods: {
    ...mapActions(['createTimeTracking', 'updateTimeTracking', 'fetchTimeTrackingsByUser']),

    async loadTimeTrackingData() {
      try {
        if (!this.getCurrentUser || !this.getCurrentUser.id) {
          console.warn('Benutzer ist nicht verfügbar. Zeiterfassungsdaten werden nicht geladen.');
          return;
        }

        // console.log('loadTimeTrackingData', this.hasRunningTimeTracking());
        await this.fetchTimeTrackingsByUser({
          userId: this.getCurrentUser.id,
          page: 0,
          pageSize: 25,
          sortField: 'createdAt',
          sortOrder: -1,
        });

        if (this.hasRunningTimeTracking()) {
          console.log('hasRunningTimeTracking', this.latestTimeTracking);
          this.startTime = this.latestTimeTracking.startTime;
          this.currentTime = new Date().toISOString();
          this.timeTrackingState = this.latestTimeTracking.timeTrackingState;
          this.timeTrackingNumber = this.latestTimeTracking.number;
        }

        // console.log('getTimeTrackings', this.getTimeTrackings);
      } catch (error) {
        console.error('Fehler beim Laden der Zeitverfolgungsdaten:', error);
      }
    },

    hasRunningTimeTracking() {
      return this.getTimeTrackings.some((tt) => tt.timeTrackingState === 'STARTED');
    },

    toggleTimeTracking() {
      if (this.timeTrackingState === 'STARTED') {
        this.stopTimeTracking();
      } else {
        this.startTimeTracking();
      }
    },

    startTimer() {
      this.timer = setInterval(() => {
        this.currentTime = new Date().toISOString();
      }, 1000);
    },

    stopTimer() {
      clearInterval(this.timer);
      this.timeTrackingState = 'STOPPED';
      this.startTime = null;
      this.currentTime = null;
      this.timeTrackingNumber = null;
    },

    async startTimeTracking() {
      const now = new Date();
      now.setMilliseconds(0);

      try {
        const response = await this.createTimeTracking({
          project: this.project,
          appointment: this.appointment,
          timeTrackingState: 'STARTED',
        });

        // Only start timer after successful API response
        this.startTime = now.toISOString();
        this.currentTime = now.toISOString();
        this.timeTrackingState = 'STARTED';
        this.startTimer();

        this.timeTrackingNumber = response;
        // localStorage.setItem('timeTrackingNumber', this.timeTrackingNumber);
        this.$nextTick(() => {
          this.loadTimeTrackingData();
        });
      } catch (error) {
        console.error('Fehler beim Starten der Zeiterfassung:', error);
        this.$toast.add({
          severity: 'error',
          summary: this.$t('ERROR'),
          detail: 'Fehler beim Starten der Zeiterfassung - Server nicht erreichbar!',
          life: 3000,
        });
      }
    },

    async stopTimeTracking() {
      if (this.timeTrackingNumber) {
        try {
          const timeTracking = await this.$store.getters.getTimeTrackingByNumber(
            this.timeTrackingNumber
          );
          if (timeTracking) {
            const stopTime = new Date().toISOString();
            this.stopTime = stopTime;

            timeTracking.stopTime = stopTime;
            timeTracking.timeTrackingState = 'STOPPED';

            await this.updateTimeTracking({
              timeTracking,
            });

            // Only stop the timer after successful update
            clearInterval(this.timer);
            this.timeTrackingState = 'STOPPED';
            this.$nextTick(() => {
              this.loadTimeTrackingData();
            });
          }
        } catch (error) {
          console.error('Fehler beim Stoppen der Zeiterfassung:', error);
          this.$toast.add({
            severity: 'error',
            summary: this.$t('ERROR'),
            detail: 'Fehler beim Stoppen der Zeiterfassung - Server nicht erreichbar!',
            life: 3000,
          });
        }
      }
    },
  },
  async created() {},

  async mounted() {
    await this.loadTimeTrackingData();
  },

  watch: {
    latestTimeTracking(newValue, oldValue) {
      // console.log('latestTimeTracking', newValue, oldValue);
      if (newValue) {
        this.startTime = newValue.startTime;
        this.timeTrackingState = newValue.timeTrackingState;
        this.timeTrackingNumber = newValue.number;
        this.currentTime = new Date().toISOString();
      }
    },
    timeTrackingState(newValue, oldValue) {
      // console.log('timeTrackingState', newValue, oldValue);
      if (newValue === 'STARTED') {
        this.startTimer();
      } else if (newValue === 'STOPPED') {
        this.stopTimer();
      }

      if (!newValue && oldValue) {
        this.stopTimer();
      }
    },
  },
};
</script>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
  gap: 0.5em;
}
.grid > div {
  padding: 1em;
  border: 2px;
}

.time-display {
  font-size: 2rem;
  margin: 0;
  margin-right: 1rem;
}

.mt-3 {
  margin-top: 1rem;
}
.section-title {
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.time-control-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 1rem;
  border: 1px solid #ccc;
  border-radius: 1px;
  margin: 0.5rem 0;
}

.button-container {
  display: flex;
  align-items: center;
}

.circular-button {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 3.3rem !important;
  height: 3.3rem !important;
  border-radius: 50% !important;
  min-width: 3.3rem !important;
  min-height: 3.3rem !important;
  padding: 0 !important;
  overflow: hidden;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  position: relative !important;
}

.circular-button:focus {
  outline: none !important;
  box-shadow: none !important;
}

.circular-button ::v-deep .p-button-icon {
  margin: 0;
  font-size: 1.2rem;
  position: absolute !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
}

.play-button ::v-deep .p-button-icon {
  left: 55% !important;
}

.stop-button ::v-deep .p-button-icon {
  left: 50% !important;
}

.btn-primary {
  background-color: var(--retis-purple, #98269f) !important;
  border-color: var(--retis-purple, #98269f) !important;
}

.btn-primary:hover {
  background-color: var(--retis-pink, #f540ff) !important;
  border-color: var(--retis-pink, #f540ff) !important;
}

.btn-danger:hover {
  background-color: #c82333 !important;
  border-color: #bd2130 !important;
}

.circular-button:focus {
  outline: none !important;
  box-shadow: none !important;
}
</style>
